import {
  DatagridConfigurable,
  List,
  SelectInput,
  TextInput,
  SelectArrayInput,
} from 'react-admin';

import { useMemo } from 'react';

import { TherapistSessionNotesCommandPanel } from './TherapistSessionNotesCommandPanel';
import { genericCSVExporter } from '@/util/genericCSVExporter';
import {
  renderExportColumns,
  renderGridColumns,
  renderOmitList,
} from '@/components/ConfigurableColumns';
import { columns } from './SessionNotesColumns';
import { ModernListActions } from '@/components/DefaultListActions';

import { DefaultPagination } from '@/components/DefaultPagination';
import { createDateFilters } from '@/util/DateFilters';

const statuses = {
  draft: 'draft',
  committed: 'committed',
  uploaded: 'uploaded',
  transcribed: 'transcribed',
  aggregated: 'aggregated',
  processed: 'processed',
} as const;

const statusChoices = Object.entries(statuses).map(([id, name]) => ({
  id,
  name,
}));

export const processingType = [
  { name: 'Manual', id: 'manual' },
  { name: 'Automatic', id: 'automatic' },
  { name: 'Both', id: 'both' },
];

const defaultTestAccountFilter = 'hidden';
const defaultDeletedAtFilter = 'hidden';
const defaultDraftTextFilter = 'hidden';

const filterDefaultValues = {
  isHiddenTestAccount: defaultTestAccountFilter,
  deletedAt: defaultDeletedAtFilter,
  draftText: defaultDraftTextFilter,
  createdAtDatePreset: 'lastWeek',
};

const filters = [
  <TextInput
    key="id"
    defaultValue=""
    source="_id"
    label="Session Note Id"
    parse={value => (value ? { $oid: value } : null)}
    format={value => value?.$oid || ''}
  />,
  <TextInput
    key="therapistId"
    defaultValue=""
    source="therapistId"
    label="Therapist Id"
  />,
  <TextInput
    key="therapistFirstName"
    defaultValue=""
    source="therapistFirstName"
    label="Therapist First Name"
  />,
  <TextInput
    key="therapistLastName"
    defaultValue=""
    source="therapistLastName"
    label="Therapist Last Name"
  />,
  <TextInput
    key="therapistEmail"
    defaultValue=""
    source="therapistEmail"
    label="Therapist Email"
    placeholder="therapist@provider.com"
  />,
  <SelectInput
    label="Processing Type"
    key="processingHistory_from"
    choices={processingType}
    source="processingHistory_from"
  />,
  <TextInput
    key="clientRecordingId"
    defaultValue=""
    source="clientRecordingId"
    label="Global Id"
    placeholder="The-dashed-id"
  />,
  <SelectArrayInput
    key="status"
    label="Status"
    choices={statusChoices}
    source="status"
    alwaysOn
  />,
  ...createDateFilters('createdAt', 'Created', {
    datePresetPinned: true,
    collapseDateRangeUnlessCustom: true,
  }),
  <SelectInput
    key="deletedAt"
    label="Deleted Items"
    source="deleted"
    alwaysOn
    emptyText="Hidden"
    emptyValue="hidden"
    choices={[
      // 'Hidden' is the empty option (emptyText/emptyValue above).
      { id: 'visible', name: 'Visible' },
      { id: 'only', name: 'Only' },
    ]}
    format={value => {
      if (!value) {
        return 'hidden';
      }

      return value;
    }}
  />,
  <SelectInput
    key="isHiddenTestAccount"
    label="Test Accounts"
    source="isHiddenTestAccount"
    emptyText="Hidden"
    emptyValue="hidden"
    alwaysOn
    choices={[
      // { id: "hidden", name: "Hidden" },
      { id: 'visible', name: 'Visible' },
      { id: 'only', name: 'Only' },
    ]}
    format={value => {
      if (!value) {
        return 'hidden';
      }

      return value;
    }}
  />,
  <SelectInput
    key="draftText"
    label="Draft Text"
    source="draftText"
    emptyText="Hidden"
    emptyValue="hidden"
    alwaysOn
    choices={[
      { id: 'visible', name: 'Visible' },
      { id: 'only', name: 'Only' },
    ]}
    format={value => {
      if (!value) {
        return 'hidden';
      }
      return value;
    }}
  />,
];

export const TherapistSessionNotesList = () => {
  const exporter = useMemo(() => documentsCsvExporter(false), []);

  return (
    <List
      perPage={25}
      actions={
        <ModernListActions
          collectionName="therapistSessionNotes"
          exporter={exporter}
        />
      }
      filterDefaultValues={filterDefaultValues}
      filters={filters}
      pagination={<DefaultPagination />}
      sort={{ field: 'createdAt', order: 'DESC' }}
      queryOptions={{
        meta: {
          facet: false,
        },
      }}
    >
      <DatagridConfigurable
        bulkActionButtons={false}
        expand={TherapistSessionNotesCommandPanel}
        omit={renderOmitList(columns(false))}
      >
        {renderGridColumns(columns(false))}
      </DatagridConfigurable>
    </List>
  );
};

const documentsCsvExporter = (hideDeleted: boolean) =>
  genericCSVExporter(
    'therapistSessionNotes',
    renderExportColumns(columns(hideDeleted)),
  );
